import React, { useMemo, useState } from 'react';
import Loader from 'components/loaderComponent';
import { useMyAppointmentsLocale } from '../pages/MyAppointments';
import ButtonComponent from 'components/button/buttonComponent';
import {
  AppointmentTypeEnum,
  useGetHistoricAppointmentCountQuery,
} from 'graphql/generated/remote-schema-hasura';
import { useModalParams } from 'components/modal/useModalManager';
import { MyAppointmentsModal } from './MyAppointmentsModal';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  MY_APPOINTMENTS_MY_VISIT_SUMMARY,
  MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT,
} from 'utilities/routes';
import MyVisitSummaryModalPatient from 'app/appointments/components/MyVisitSummaryModalPatient';
import { componentIds, PARAM_MODALS_IDENTIFIERS } from 'utilities/constants';
import {
  UpcomingAppointmentsProvider,
  upcomingStatuses,
} from 'app/my-appointments/components/UpcomingAppointments';
import { AppointmentHistoryProvider } from 'app/my-appointments/components/AppointmentHistory';
import { CancelAppointmentModal } from 'components/cancelAppointmentModal/CancelAppointmentModal';
import { EditAppointmentModal } from 'app/my-appointments/components/EditAppointmentModal';
import AppointmentDetailModalPatient from 'components/appointmentDetailModal/AppointmentDetailModalPatient';
import UpcomingAppointmentsWrapper from './upcoming-appointments/UpcomingAppointmentsWrapper';
import PastAppointmentWrapper from './past-appointments/PastAppointmentWrapper';
import { FIND_A_PROVIDER } from './scheduled-a-appointment/routePath';
import { useGetComponent } from 'hooks/useGetComponent';
import { Step } from 'react-joyride';
import Tour from 'components/Tour';
import { AppTourTypes } from 'graphql/generated/hasura';
import AcceptAiModal from './vonage-room-v2/components/AcceptAIModal';

const today = new Date().toISOString();

export const PatientAppointmentsMainPage = () => {
  const { isOpen: isAppointmentVideoModal } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.VIDEO_APPOINTMENT_MODAL,
  );
  const { isOpen: isAppointmentChatModal } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.CHAT_APPOINTMENT_MODAL,
  );
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment-id');
  const { loading, locale } = useMyAppointmentsLocale();

  const [isCheckboxChecked, setIsCheckboxChecked] = React.useState(false);

  const { data: localeTour, loading: loadingTourData } = useGetComponent({
    locale: 'en',
    componentId: componentIds.APPOINTMENT_TOUR,
  });

  const navigate = useNavigate();
  const { isOpen: isMyVisitSummaryModalOpen } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.MY_VISIT_SUMMARY_MODAL_PATIENT_MODAL_ID,
  );
  const { isOpen: isCancelAppointmentOpen } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.CANCEL_APPOINTMENT_MODAL,
  );
  const { isOpen: isViewAppointmentDetailsModal } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.VIEW_APPOINTMENT_DETAILS_MODAL_PATIENT,
  );
  const { isOpen: isEditAppointmentModal } = useModalParams('edit_appointment');

  const location = useLocation();

  const [hasHistoryAppointments, setHasAppointments] = useState<boolean>(false);

  const [ready, setReady] = useState(false);

  const onLoaded = () => {
    if (ready) return null;

    setReady(true);
    return null;
  };

  const steps = useMemo(
    () =>
      [
        {
          disableBeacon: true,
          target: '[data-tour="upcoming-appointments"]',
          placement: 'bottom-start',
          title: localeTour?.upcomingAppointments?.title,
          content: localeTour?.upcomingAppointments?.content,
        },
        {
          target: '[data-tour="past-appointments"]',
          title: localeTour?.pastAppointments?.title,
          content: localeTour?.pastAppointments?.content,
          placement: 'top-start',
          data: {
            className: 'mt-0, -mb-2.5',
          },
        },
      ] as Step[],
    [localeTour],
  );

  const handleScheduleAnAppointment = () => {
    navigate(`${MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT}/${FIND_A_PROVIDER}`);
  };

  const handleMyVisitSummaryClick = (appointmentId: string) => {
    navigate(`${MY_APPOINTMENTS_MY_VISIT_SUMMARY}/${appointmentId}`);
  };

  const { loading: patientCountLoading } = useGetHistoricAppointmentCountQuery({
    fetchPolicy: 'network-only',
    variables: {
      from: today,
      appointmentStatus: upcomingStatuses,
    },
    onError: () => setHasAppointments(false),
    onCompleted: (data) => {
      setHasAppointments(
        !!(data.getFHIRAppointmentByRequestPatientCodexId.total ?? 0),
      );
    },
  });

  if (location.state?.appointmentComplete) {
    return (
      <MyAppointmentsModal
        appointmentType={AppointmentTypeEnum.Chat}
        appointmentId={appointmentId}
        isPatient
        appointmentComplete
      />
    );
  }

  if (
    (isAppointmentVideoModal || isAppointmentChatModal) &&
    !isCheckboxChecked
  ) {
    return (
      <AcceptAiModal
        setIsCheckboxChecked={setIsCheckboxChecked}
        isOpen={isAppointmentChatModal || isAppointmentVideoModal}
      />
    );
  }

  if (isAppointmentChatModal) {
    return (
      <MyAppointmentsModal
        appointmentType={AppointmentTypeEnum.Chat}
        appointmentId={appointmentId}
        isPatient
      />
    );
  }

  if (isAppointmentVideoModal) {
    return (
      <MyAppointmentsModal
        appointmentType={AppointmentTypeEnum.Video}
        appointmentId={appointmentId}
        isPatient
      />
    );
  }

  if (!locale || loading || patientCountLoading || loadingTourData) {
    return <Loader />;
  }

  return (
    <>
      {isMyVisitSummaryModalOpen && <MyVisitSummaryModalPatient />}
      {isViewAppointmentDetailsModal && <AppointmentDetailModalPatient />}
      {isCancelAppointmentOpen && <CancelAppointmentModal />}
      {isEditAppointmentModal && <EditAppointmentModal />}
      <div
        className="px-0 pt-30 desktop:pt-0"
        data-testid="my-appointsmets-page"
      >
        <div className="flex flex-col gap-30">
          <div className="flex desktop:flex-row w-full justify-between items-start p-0 gap-2.5 desktop:gap-30 desktop:mb-[34px]">
            {hasHistoryAppointments && (
              <div className="flex max-desktop:flex-col desktop:flex-row justify-between items-start w-full">
                <div>
                  <p className="text-dark-gray font-medium text-h1">
                    {locale.myAppointmentsTabName}
                  </p>
                </div>
                <div className="flex max-desktop:w-full desktop:items-center desktop:flex-row flex-col max-desktop:mt-4 desktop:justify-end">
                  <ButtonComponent
                    className="max-h-[45px] !text-sm"
                    onClick={handleScheduleAnAppointment}
                    testID="schedule-appointment-button"
                  >
                    {locale.scheduleAnAppointment}
                  </ButtonComponent>
                </div>
              </div>
            )}
          </div>
          <>
            <UpcomingAppointmentsProvider>
              <UpcomingAppointmentsWrapper />
            </UpcomingAppointmentsProvider>
            <AppointmentHistoryProvider
              handleMyVisitSummaryClick={handleMyVisitSummaryClick}
            >
              <PastAppointmentWrapper />
            </AppointmentHistoryProvider>
            {onLoaded()}
          </>
        </div>
      </div>

      {ready && !loadingTourData && (
        <Tour
          allowOnceByName={AppTourTypes.PatientAppointments}
          steps={steps}
        />
      )}
    </>
  );
};
